import { CloseIcon, Header, IconButton, Paragraph } from "@datagame/ui";
import { Dialog } from "@headlessui/react";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { LIBRARY_IMAGES_QUERY } from "../../../../graphql/queries/LibraryImageQueries";
import { LibraryImage, LibraryImageKind } from "../../../../gql/graphql";
import { EmptyList } from "../../EmptyList";

import styles from "./LibraryModal.module.scss";

interface LibraryModalProps {
  onClose: () => void;
  kind: LibraryImageKind;
  onSelect: (image: LibraryImage) => void;
}

export const LibraryModal = ({ onClose, kind, onSelect }: LibraryModalProps) => {
  const { t } = useTranslation();

  const { data } = useQuery(LIBRARY_IMAGES_QUERY);

  const filteredImages = useMemo(() => {
    const images = data?.libraryImageAll as LibraryImage[];
    if (!images) {
      return [];
    }

    return images.filter((image: LibraryImage) => image.kind === kind);
  }, [data?.libraryImageAll, kind]);

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.headerWrapper}>
            <Header className={styles.header} variant="h6">
              {t("library:modal.title")}
            </Header>

            <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
          </div>
          <Paragraph color="secondary" size="small" className={styles.description}>
            {t("library:modal.description")}
          </Paragraph>

          <div className={styles.images}>
            {filteredImages.length === 0 && (
              <div className={styles.emptyWrapper}>
                <EmptyList title={t("library:modal.noImages")} description={t("library:modal.noImagesDescription")} />
              </div>
            )}
            {filteredImages.map((image: LibraryImage) => (
              <div
                key={image.id}
                className={styles.imageWrapper}
                onClick={() => onSelect(image)}
                tabIndex={0}
                onKeyDown={() => onSelect(image)}
                role="button"
              >
                <img key={image.id} className={styles.image} src={image.url} alt={image.name} />
              </div>
            ))}
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
